// ===============================================================
// Creator Payout Policy Lite — LIVORA
// path: src/pages/withdraw/WithdrawHelpPage.js
// ===============================================================

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TopBar2 from "../../components/common/TopBar2";
import { buildSupportNewTicketPath } from "../../support/supportRoutes";
import "./WithdrawHelpPage.css";
import { ENABLE_REAL_MONEY_PAYOUTS } from "../../config/featureFlags";

const DEFAULT_OPEN = "requirements";

function HelpSectionBody({ bulletsKey, defaultBullets }) {
  const { t } = useTranslation();
  const bullets = useMemo(() => {
    const raw = t(bulletsKey, { returnObjects: true, defaultValue: defaultBullets });
    return Array.isArray(raw) ? raw : defaultBullets;
  }, [t, bulletsKey, defaultBullets]);

  return (
    <ul className="withdraw-help-card__list">
      {bullets.map((line, idx) => (
        <li key={`${idx}-${line.slice(0, 24)}`}>{line}</li>
      ))}
    </ul>
  );
}

export default function WithdrawHelpPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openKey, setOpenKey] = useState(DEFAULT_OPEN);

  const items = useMemo(
    () => [
      {
        key: "requirements",
        title: t("withdraw.help.sections.requirements.title", {
          defaultValue: "متطلبات السحب",
        }),
        bulletsKey: "withdraw.help.sections.requirements.bullets",
        defaultBullets: [
          "طلبات صرف المستحقات متاحة للحسابات المؤهلة كصانعات محتوى على Livora.",
          "يجب إكمال التحقق من الحساب واعتماده قبل إرسال طلب السحب.",
          "يجب أن يصل رصيدك القابل للسحب إلى الحد الأدنى المعروض داخل التطبيق.",
          "يُسمح بطلب سحب نشط واحد فقط في كل مرة.",
          "قد تنطبق فترة انتظار بين الطلبات بعد إرسال طلب سابق.",
          "عند استيفاء الشروط تصبحين مؤهلة لإرسال طلب — وهذا لا يعني استلام الأموال فورًا.",
        ],
      },
      {
        key: "recipient",
        title: t("withdraw.help.sections.recipient.title", {
          defaultValue: "بيانات الاستلام",
        }),
        bulletsKey: "withdraw.help.sections.recipient.bullets",
        defaultBullets: [
          "أدخلي الاسم القانوني للمستلمة ودولة الاستلام في قسم بيانات الاستلام.",
          "تأكدي من دقة تفاصيل الاستلام حسب الطريقة المختارة (محفظة، PayPal، أو بنك).",
          "أكدي أن البيانات صحيحة وأنكِ صاحبة الحساب.",
          "راجعي كل الحقول قبل الإرسال — قد لا يمكن تعديل الطلب لاحقًا.",
          "قد تُعبأ بعض البيانات تلقائيًا من طلب سابق؛ تحققي منها دائمًا.",
        ],
      },
      {
        key: "crypto",
        title: t("withdraw.help.sections.crypto.title", {
          defaultValue: "تحذير صرف الكريبتو",
        }),
        bulletsKey: "withdraw.help.sections.crypto.bullets",
        defaultBullets: [
          "يُرسل الصرف بالعملة USDT على شبكة TRC20 فقط، كما هو موضح في التطبيق.",
          "تحويلات البلوكشين لا يمكن عكسها بعد الإرسال.",
          "الإرسال إلى عنوان أو شبكة خاطئة قد يؤدي إلى فقدان دائم للأموال.",
          "استخدمي محفظة تتحكمين بها فقط. لا يمكن استرداد أموال أُرسلت إلى بيانات خاطئة.",
        ],
      },
      {
        key: "review",
        title: t("withdraw.help.sections.review.title", {
          defaultValue: "المراجعة والتحقق الأمني",
        }),
        bulletsKey: "withdraw.help.sections.review.bullets",
        defaultBullets: [
          "تُراجع جميع الطلبات يدويًا قبل إتمام الصرف.",
          "عادةً تستغرق المعالجة بضعة أيام عمل، وقد تطول حسب الضغط أو التحقق الأمني.",
          "قد يُؤجل الطلب أو يُرفض إذا كانت البيانات غير صحيحة أو الأهلية غير مكتملة أو وُجدت مخاوف أمنية.",
          "تابعي الحالة من سجل السحب وتواصلي مع الدعم لأي استفسار عن الصرف.",
        ],
      },
      {
        key: "records",
        title: t("withdraw.help.sections.records.title", {
          defaultValue: "السجلات والمحاسبة",
        }),
        bulletsKey: "withdraw.help.sections.records.bullets",
        defaultBullets: [
          "راجعي الطلبات السابقة وحالاتها من سجل السحب.",
          "عند إتمام الصرف قد يظهر مرجع دفع على الطلب المدفوع.",
          "احتفظي بسجلاتك الخاصة للمحاسبة والإقرارات الضريبية في بلدك.",
          "قيم الدولار المعروضة في التطبيق تقديرية للتسهيل وليست سعر صرف مضمونًا.",
        ],
      },
    ],
    [t]
  );

  function toggleItem(key) {
    setOpenKey((prev) => (prev === key ? "" : key));
  }

  // Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: while real-money payouts are
  // disabled, do not show bank/PayPal/USDT/crypto/cash-out instructions -
  // the original accordion content (items/HelpSectionBody) above is
  // preserved unchanged (not deleted), just not rendered here. Support
  // access (a historical-support pathway, not a payout action) is kept.
  if (!ENABLE_REAL_MONEY_PAYOUTS) {
    return (
      <div className="withdraw-help-page">
        <TopBar2
          title={t("withdraw.help.title", {
            defaultValue: "سياسة صرف المستحقات",
          })}
          onBack={() => navigate(-1)}
        />

        <div className="withdraw-help-page__spacer" />

        <div className="withdraw-help-page__content">
          <div className="withdraw-help-hero">
            <h1 className="withdraw-help-hero__title">
              {t("withdraw.help.title", {
                defaultValue: "سياسة صرف المستحقات",
              })}
            </h1>

            <p className="withdraw-help-hero__subtitle">
              {t("withdraw.help.payoutsDisabledNotice", {
                defaultValue:
                  "Cash withdrawals are currently unavailable. Tokens are virtual and cannot be redeemed for cash.",
              })}
            </p>
          </div>

          <div className="withdraw-help-note-card">
            <div className="withdraw-help-note-card__title">
              {t("withdraw.help.supportTitle", {
                defaultValue: "تحتاجين مساعدة؟",
              })}
            </div>

            <div className="withdraw-help-note-card__text">
              {t("withdraw.help.supportText", {
                defaultValue:
                  "إذا كان لديك سؤال عن طلب صرف أو حالة طلب، تواصلي مع الدعم من داخل التطبيق.",
              })}
            </div>

            <button
              type="button"
              className="withdraw-help-note-card__btn"
              onClick={() =>
                navigate(
                  buildSupportNewTicketPath({ type: "issue", category: "withdrawal" })
                )
              }
            >
              {t("withdraw.help.supportButton", {
                defaultValue: "فتح تذكرة دعم",
              })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="withdraw-help-page">
      <TopBar2
        title={t("withdraw.help.title", {
          defaultValue: "سياسة صرف المستحقات",
        })}
        onBack={() => navigate(-1)}
      />

      <div className="withdraw-help-page__spacer" />

      <div className="withdraw-help-page__content">
        <div className="withdraw-help-hero">
          <h1 className="withdraw-help-hero__title">
            {t("withdraw.help.title", {
              defaultValue: "سياسة صرف المستحقات",
            })}
          </h1>

          <p className="withdraw-help-hero__subtitle">
            {t("withdraw.help.subtitle", {
              defaultValue:
                "ملخص تشغيلي لطلبات صرف المستحقات على Livora: الشروط، بيانات الاستلام، والمراجعة.",
            })}
          </p>
        </div>

        <div className="withdraw-help-list">
          {items.map((item) => {
            const isOpen = openKey === item.key;

            return (
              <div
                key={item.key}
                className={`withdraw-help-card ${isOpen ? "is-open" : ""}`}
              >
                <button
                  type="button"
                  className="withdraw-help-card__head"
                  onClick={() => toggleItem(item.key)}
                >
                  <span className="withdraw-help-card__title">{item.title}</span>
                  <span className="withdraw-help-card__icon">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>

                {isOpen ? (
                  <div className="withdraw-help-card__body">
                    <HelpSectionBody
                      bulletsKey={item.bulletsKey}
                      defaultBullets={item.defaultBullets}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <p className="withdraw-help-footer-note">
          {t("withdraw.help.footerNote", {
            defaultValue:
              "تلخّص هذه الصفحة عمليات الصرف داخل Livora. قد تختلف الشروط القانونية والمسؤوليات الضريبية حسب البلد.",
          })}
        </p>

        <div className="withdraw-help-note-card">
          <div className="withdraw-help-note-card__title">
            {t("withdraw.help.supportTitle", {
              defaultValue: "تحتاجين مساعدة؟",
            })}
          </div>

          <div className="withdraw-help-note-card__text">
            {t("withdraw.help.supportText", {
              defaultValue:
                "إذا كان لديك سؤال عن طلب صرف أو حالة طلب، تواصلي مع الدعم من داخل التطبيق.",
            })}
          </div>

          <button
            type="button"
            className="withdraw-help-note-card__btn"
            onClick={() =>
              navigate(
                buildSupportNewTicketPath({ type: "issue", category: "withdrawal" })
              )
            }
          >
            {t("withdraw.help.supportButton", {
              defaultValue: "فتح تذكرة دعم",
            })}
          </button>
        </div>
      </div>
    </div>
  );
}
